'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Bell, BookmarkPlus, ChevronDown, SlidersHorizontal, Heart, Layers, FileText } from 'lucide-react';
import GlobalSearchBar from './GlobalSearchBar';
import LocationRadiusControl from './LocationRadiusControl';
import SaveSearchDialog from './SaveSearchDialog';
import { usePlanningContext } from '../state/PlanningContext';
import { trackPlanningUpdatesEvent } from '../../../lib/planningUpdatesAnalytics';
import { PLANNING_ALERTS_ENABLED } from '@/lib/planningAlertsFeatureFlag';
import { consumePlanningAlertResumeMarker } from '@/lib/pendingPlanningAlert';

interface SearchPanelProps {
  className?: string;
  tutorialMode?: boolean;
  onTutorialAction?: (topicKey: string) => void;
  defaultShowAdvanced?: boolean;
  analyticsPlacement?: string;
  compact?: boolean;
}

const isSameRange = (left: { min: number; max: number } | null | undefined, right: { min: number; max: number } | null | undefined) => (
  Number(left?.min) === Number(right?.min) && Number(left?.max) === Number(right?.max)
);

const SearchPanel: React.FC<SearchPanelProps> = ({
  className,
  tutorialMode = false,
  onTutorialAction,
  defaultShowAdvanced = true,
  analyticsPlacement = 'search_panel',
  compact = false,
}) => {
  const [showAdvanced, setShowAdvanced] = useState(defaultShowAdvanced);
  const [saveOpen, setSaveOpen] = useState(false);
  const [dialogIntent, setDialogIntent] = useState<'save' | 'alert'>('save');
  const {
    selectedFilter,
    setSelectedFilter,
    favorites,
    selectedScaleCategory,
    setSelectedScaleCategory,
    scaleRange,
    setScaleRange,
    showOnlyGroupedApplications,
    setShowOnlyGroupedApplications,
    showAiSummaries,
    toggleAiSummaries,
  } = usePlanningContext();

  const scaleApplyDebounceTimerRef = useRef<number | null>(null);
  const scaleApplyMaxTimerRef = useRef<number | null>(null);
  const pendingRangeRef = useRef(scaleRange);
  const pendingCategoryRef = useRef(selectedScaleCategory || 'all');
  const appliedRangeRef = useRef(scaleRange);
  const appliedCategoryRef = useRef(selectedScaleCategory || 'all');

  useEffect(() => {
    if (!PLANNING_ALERTS_ENABLED || !consumePlanningAlertResumeMarker()) return;
    setDialogIntent('alert');
    setSaveOpen(true);
  }, []);

  useEffect(() => {
    pendingRangeRef.current = scaleRange;
    appliedRangeRef.current = scaleRange;
  }, [scaleRange]);

  useEffect(() => {
    const nextCategory = selectedScaleCategory || 'all';
    pendingCategoryRef.current = nextCategory;
    appliedCategoryRef.current = nextCategory;
  }, [selectedScaleCategory]);

  const clearScaleApplyTimers = useCallback(() => {
    if (scaleApplyDebounceTimerRef.current != null) {
      window.clearTimeout(scaleApplyDebounceTimerRef.current);
      scaleApplyDebounceTimerRef.current = null;
    }

    if (scaleApplyMaxTimerRef.current != null) {
      window.clearTimeout(scaleApplyMaxTimerRef.current);
      scaleApplyMaxTimerRef.current = null;
    }
  }, []);

  const applyPendingScaleFiltersNow = useCallback(() => {
    clearScaleApplyTimers();
    const pendingRange = pendingRangeRef.current;
    const pendingCategory = pendingCategoryRef.current || 'all';

    if (!isSameRange(appliedRangeRef.current, pendingRange)) {
      setScaleRange(pendingRange);
      appliedRangeRef.current = pendingRange;
    }

    if ((appliedCategoryRef.current || 'all') !== pendingCategory) {
      setSelectedScaleCategory(pendingCategory);
      appliedCategoryRef.current = pendingCategory;
    }
  }, [clearScaleApplyTimers, setScaleRange, setSelectedScaleCategory]);

  const applyScaleSelectionNow = useCallback((nextRange: { min: number; max: number }, nextCategory: string) => {
    const normalizedCategory = nextCategory || 'all';

    clearScaleApplyTimers();
    pendingRangeRef.current = nextRange;
    pendingCategoryRef.current = normalizedCategory;
    appliedRangeRef.current = nextRange;
    appliedCategoryRef.current = normalizedCategory;
    setScaleRange(nextRange);
    setSelectedScaleCategory(normalizedCategory);
  }, [clearScaleApplyTimers, setScaleRange, setSelectedScaleCategory]);

  useEffect(() => () => {
    applyPendingScaleFiltersNow();
  }, [applyPendingScaleFiltersNow]);

  const appliedScaleMin = Number(scaleRange?.min);
  const appliedScaleMax = Number(scaleRange?.max);
  const isScaleFivePlus = appliedScaleMin === 5 && appliedScaleMax === 10;
  const isMajorScale = appliedScaleMin === 7 && appliedScaleMax === 10;

  const quickButtonBase = 'pr-chip whitespace-nowrap';

  const trackSearchPanelAction = useCallback((
    action: string,
    status: string,
    metadata: Record<string, unknown> = {},
  ) => {
    trackPlanningUpdatesEvent(action, {
      placement: analyticsPlacement,
      status,
      metadata: {
        selected_filter: selectedFilter,
        scale_min: appliedScaleMin,
        scale_max: appliedScaleMax,
        grouped_only: showOnlyGroupedApplications,
        ai_summaries: showAiSummaries,
        advanced_visible: showAdvanced,
        ...metadata
      }
    });
  }, [
    appliedScaleMax,
    appliedScaleMin,
    analyticsPlacement,
    selectedFilter,
    showAdvanced,
    showAiSummaries,
    showOnlyGroupedApplications
  ]);

  const emitTutorialAction = (topicKey: string) => {
    if (!tutorialMode || typeof onTutorialAction !== 'function') return;
    onTutorialAction(topicKey);
  };

  return (
    <section
      className={`pr-panel relative z-20 space-y-5 border-t-2 border-t-[var(--c-text-primary)] px-4 py-4 sm:px-5 sm:py-5 ${className ?? ''}`}
    >
      <GlobalSearchBar />

      <div className="border-t border-[var(--c-border)] pt-4">
        <p className="pr-section-label mb-3">Quick filters</p>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            aria-pressed={isMajorScale}
            onClick={() => {
              const nextState = isMajorScale
                ? { range: { min: 1, max: 10 }, category: 'all' }
                : { range: { min: 7, max: 10 }, category: 'all' };

              trackSearchPanelAction('planning_updates_major_filter_clicked', isMajorScale ? 'disabled' : 'enabled', {
                quick_filter: 'major',
                next_scale_min: nextState.range.min,
                next_scale_max: nextState.range.max
              });
              applyScaleSelectionNow(nextState.range, nextState.category);
              emitTutorialAction('major');
            }}
            className={quickButtonBase}
          >
            Major
          </button>

          <button
            type="button"
            aria-pressed={selectedFilter === 'favorites'}
            onClick={() => {
              const nextFilter = selectedFilter === 'favorites' ? 'all' : 'favorites';
              trackSearchPanelAction('planning_updates_quick_filter_selected', nextFilter, {
                quick_filter: 'favorites',
                previous_filter: selectedFilter,
                next_filter: nextFilter,
                favorite_count: favorites.length
              });
              setSelectedFilter(nextFilter);
              emitTutorialAction('favorites');
            }}
            className={quickButtonBase}
          >
            <Heart className={`h-3.5 w-3.5 ${selectedFilter === 'favorites' ? 'fill-current' : ''}`} />
            Favourites
            {favorites.length > 0 && (
              <span className="pr-chip-count">{favorites.length}</span>
            )}
          </button>

          <button
            type="button"
            aria-pressed={isScaleFivePlus}
            onClick={() => {
              const nextState = isScaleFivePlus
                ? { range: { min: 1, max: 10 }, category: 'all' }
                : { range: { min: 5, max: 10 }, category: 'all' };

              trackSearchPanelAction('planning_updates_scale_5_plus_clicked', isScaleFivePlus ? 'disabled' : 'enabled', {
                quick_filter: 'scale_5_plus',
                next_scale_min: nextState.range.min,
                next_scale_max: nextState.range.max
              });
              applyScaleSelectionNow(nextState.range, nextState.category);
              emitTutorialAction('scaleRange');
            }}
            className={quickButtonBase}
          >
            Scale 5+
          </button>

          <button
            type="button"
            aria-pressed={showOnlyGroupedApplications}
            onClick={() => {
              trackSearchPanelAction('planning_updates_grouped_filter_clicked', showOnlyGroupedApplications ? 'disabled' : 'enabled', {
                quick_filter: 'grouped',
                enabled: !showOnlyGroupedApplications
              });
              setShowOnlyGroupedApplications(prev => !prev);
              emitTutorialAction('grouped');
            }}
            className={quickButtonBase}
          >
            <Layers className="h-3.5 w-3.5" />
            Grouped
          </button>

          <button
            type="button"
            aria-pressed={showAiSummaries}
            onClick={() => {
              trackSearchPanelAction('planning_updates_ai_summaries_toggled', showAiSummaries ? 'disabled' : 'enabled', {
                enabled: !showAiSummaries
              });
              toggleAiSummaries();
              emitTutorialAction('aiSummaries');
            }}
            className={quickButtonBase}
          >
            <FileText className="h-3.5 w-3.5" />
            AI summaries
          </button>
        </div>

        <div className="flex w-full flex-wrap items-center gap-x-1 gap-y-2 border-t border-[var(--c-border)] pt-3 lg:w-auto lg:border-t-0 lg:pt-0">
          {PLANNING_ALERTS_ENABLED && (
            <button
              type="button"
              onClick={() => {
                setDialogIntent('alert');
                setSaveOpen(true);
                trackSearchPanelAction('planning_alert_create_started', 'started', {
                  placement: analyticsPlacement
                });
              }}
              className="pr-textlink"
            >
              <Bell className="h-3.5 w-3.5" aria-hidden />
              Create alert
            </button>
          )}
          <span className="hidden h-4 w-px bg-[var(--c-border)] lg:inline-block" aria-hidden />
          <button
            type="button"
            onClick={() => {
              setDialogIntent('save');
              setSaveOpen(true);
              trackSearchPanelAction('planning_updates_save_search_started', 'started', {
                placement: analyticsPlacement
              });
            }}
            className="pr-textlink"
          >
            <BookmarkPlus className="h-3.5 w-3.5" aria-hidden />
            Save search
          </button>
          <span className="hidden h-4 w-px bg-[var(--c-border)] lg:inline-block" aria-hidden />
          <button
            type="button"
            onClick={() => {
              trackSearchPanelAction('planning_updates_advanced_options_toggled', showAdvanced ? 'hidden' : 'shown', {
                previous_advanced_visible: showAdvanced,
                next_advanced_visible: !showAdvanced
              });
              setShowAdvanced(prev => !prev);
              emitTutorialAction('searchArea');
            }}
            aria-expanded={showAdvanced}
            className={`pr-textlink ${showAdvanced ? 'text-[var(--c-accent)]' : ''}`}
          >
            <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden />
            <span>{showAdvanced ? 'Hide search area' : 'Search area'}</span>
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} aria-hidden />
          </button>
        </div>
        </div>
      </div>

      <AnimatePresence initial={false}>
        {showAdvanced && (
          <motion.div
            key="advanced-search-area"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="overflow-hidden"
          >
            <div className="border-t border-[var(--c-border)] pt-4">
              <p className="pr-section-label mb-4">Search area</p>
              <LocationRadiusControl variant="panel" showHeading={false} compact={compact} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <SaveSearchDialog open={saveOpen} onClose={() => setSaveOpen(false)} initialIntent={dialogIntent} />
    </section>
  );
};

export default SearchPanel;
